import { useState, useEffect, useCallback } from 'react';
import { UserCog, Plus, X, Shield, Key } from 'lucide-react';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminTeam() {
  const [agents, setAgents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/team`, { credentials: 'include' });
      if (res.ok) setAgents((await res.json()).agents || []);
    } catch (err) { logger.error("Operation failed:", err); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const createAgent = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/admin/team`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) { setShowCreate(false); setForm({ name: '', email: '', password: '' }); fetchAgents(); }
    } catch (err) { logger.error("Operation failed:", err); }
    setCreating(false);
  };

  const toggleAgent = async (id, isActive) => {
    await fetch(`${API}/api/admin/team/${id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !isActive })
    });
    fetchAgents();
  };

  return (
    <div data-testid="admin-team" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Support Team</h1>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-lg text-sm font-medium">
          <Plus className="w-4 h-4" /> Add Agent
        </button>
      </div>

      {showCreate && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold">Create Support Agent</h2>
            <button onClick={() => setShowCreate(false)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name"
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" type="email"
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none" />
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" type="password"
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none" />
          </div>
          <button onClick={createAgent} disabled={creating || !form.name || !form.email || !form.password}
            className="px-6 py-2 bg-cyan-600 text-white rounded-lg text-sm disabled:opacity-30">{creating ? 'Creating...' : 'Create Agent'}</button>
        </div>
      )}

      <div className="space-y-3">
        {agents.map(a => (
          <div key={a.id} className="bg-[#0E1830] border border-white/10 rounded-xl p-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${a.is_active ? 'bg-emerald-500/20' : 'bg-white/5'}`}>
                <UserCog className={`w-5 h-5 ${a.is_active ? 'text-emerald-400' : 'text-white/30'}`} />
              </div>
              <div>
                <div className="text-white font-medium">{a.name}</div>
                <div className="text-white/40 text-sm">{a.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-white/40 text-xs">Tickets handled</div>
                <div className="text-white text-sm">{a.tickets_handled || 0}</div>
              </div>
              <div className={`text-xs px-2 py-1 rounded ${a.status === 'online' ? 'bg-green-500/20 text-green-400' : a.status === 'away' ? 'bg-amber-500/20 text-amber-400' : 'bg-white/10 text-white/40'}`}>
                {a.status || 'offline'}
              </div>
              <button onClick={() => toggleAgent(a.id, a.is_active)}
                className={`text-xs px-3 py-1 rounded border ${a.is_active ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-green-500/30 text-green-400 hover:bg-green-500/10'}`}>
                {a.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
        {agents.length === 0 && <p className="text-white/40 text-center py-8">No support agents created yet</p>}
      </div>
    </div>
  );
}
